<template>
  <div id="app">
    <nav class="navbar navbar-expand-lg navbar-light mb-4" style="background-color: orange;">
      <div class="container-fluid">
        <router-link class="navbar-brand fw-semibold" to="/overview">
          <img :src="logoImg" alt="Goodwill Logo" height="60" />
        </router-link>



        <button class="navbar-toggler" type="button" data-bs-toggle="collapse" data-bs-target="#navbarNav"
          aria-controls="navbarNav" aria-expanded="false" aria-label="Toggle navigation">
          <span class="navbar-toggler-icon"></span>
        </button>




        <div class="collapse navbar-collapse" id="navbarNav">
          <ul v-if="isLoggedIn" class="navbar-nav ms-auto">



            <button v-if="isLoggedIn" type="button" class="btn notification-bell"
              :class="{ 'text-muted': !notificationsEnabled }" :title="notificationsEnabled
                ? 'Disable notifications'
                : 'Enable notifications'
                " :aria-label="notificationsEnabled
                  ? 'Disable notifications'
                  : 'Enable notifications'
                  " :aria-pressed="notificationsEnabled" @click="toggleNotifications">
              <i :class="notificationsEnabled
                ? 'bi bi-bell-fill'
                : 'bi bi-bell-slash'
                "></i>
            </button>




            <li class="nav-item">
              <router-link class="nav-link" to="/overview">
                Overview
              </router-link>
            </li>

            <li class="nav-item">
              <router-link class="nav-link" to="/metrics">
                Metrics
              </router-link>
            </li>

            <li v-if="userType === 'admin' || userType === 'authorizer'" class="nav-item">
              <router-link class="nav-link" to="/admin/fulfillment">
                Fulfillment
              </router-link>
            </li>

            <li class="nav-item">
              <router-link class="nav-link" to="/my-donations">
                My Requests
              </router-link>
            </li>

            <li class="nav-item">
              <a class="nav-link" href="#" @click.prevent="logout">
                Logout
              </a>
            </li>
          </ul>
        </div>
      </div>
    </nav>

    <router-view />
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import axios from 'axios';
import { Collapse } from 'bootstrap';

const logoImg = `${process.env.BASE_URL || '/'}img/GSD_logo.jpg`;
const API_URL = process.env.VUE_APP_API_URL;

const router = useRouter();

const isLoggedIn = ref(false);
const userType = ref('');
const notificationsEnabled = ref(false);

let notificationIdentity = '';
let audioContext = null;
let removeRouterAfterEach = null;

function getToken() {
  return sessionStorage.getItem('token') || '';
}

function getUserType() {
  return sessionStorage.getItem('userType') || '';
}

function getAuthHeaders(token = getToken()) {
  if (!token) return {};

  return {
    Authorization: `Bearer ${token}`
  };
}

function getNotificationIdentity() {
  const token = getToken();
  const role = getUserType();

  if (!token || !role) return '';

  try {
    const tokenParts = token.split('.');

    if (tokenParts.length !== 3) return '';

    const base64Payload = tokenParts[1]
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const payload = JSON.parse(atob(base64Payload));

    return `${payload.id || payload.username}:${role}`;
  } catch {
    return '';
  }
}

function notificationPreferenceKey() {
  const identity = getNotificationIdentity();

  return identity
    ? `notifications-disabled:${identity}`
    : '';
}

function unlockAudio() {
  try {
    const AudioContextClass =
      window.AudioContext || window.webkitAudioContext;

    if (!AudioContextClass) return;

    if (!audioContext) {
      audioContext = new AudioContextClass();
    }

    if (audioContext.state === 'suspended') {
      audioContext.resume();
    }
  } catch {
     // Audio playback is optional.
  }
}

function urlBase64ToUint8Array(value) {
  const padding = '='.repeat((4 - (value.length % 4)) % 4);

  const base64 = (value + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);

  return Uint8Array.from(
    [...rawData].map(character => character.charCodeAt(0))
  );
}

async function enableNotificationsOnServer(token = getToken()) {
  if (!token) return;

  await axios.post(
    `${API_URL}/api/push/enable`,
    {},
    {
      headers: getAuthHeaders(token)
    }
  );
}

async function disableNotificationsOnServer(token = getToken()) {
  if (!token) return;

  await axios.post(
    `${API_URL}/api/push/disable`,
    {},
    {
      headers: getAuthHeaders(token)
    }
  );
}

async function registerPushSubscription() {
  const token = getToken();

  if (!token) return;
  if (!notificationsEnabled.value) return;
  if (!('serviceWorker' in navigator)) return;
  if (!('PushManager' in window)) return;
  if (!('Notification' in window)) return;
  if (Notification.permission !== 'granted') return;

  try {
    // console.log('Registering service worker...');

    const registration =
      await navigator.serviceWorker.register('/sw.js');

    await navigator.serviceWorker.ready;

    // console.log('Service worker ready:', registration.scope);

    const { data } = await axios.get(
      `${API_URL}/api/push/public-key`,
      {
        headers: getAuthHeaders(token)
      }
    );

    // console.log('Received VAPID key:', data.publicKey);

    let subscription =
      await registration.pushManager.getSubscription();

    if (!subscription) {
      // console.log('Creating push subscription...');

      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.publicKey)
      });
    }

    await axios.post(
      `${API_URL}/api/push/subscribe`,
      subscription.toJSON(),
      {
        headers: getAuthHeaders(token)
      }
    );

    // console.log('Push subscription saved successfully');
  } catch (error) {
    console.warn(
      'Could not register push subscription:',
      error.response?.data || error.message
    );
  }
}


async function removePushSubscription() {
  if (!('serviceWorker' in navigator)) return;

  const token = getToken();

  if (!token) return;

  try {
    const registration =
      await navigator.serviceWorker.getRegistration('/sw.js');

    const subscription =
      await registration?.pushManager.getSubscription();

    if (!subscription) return;

    try {
      await axios.delete(
        `${API_URL}/api/push/subscribe`,
        {
          data: {
            endpoint: subscription.endpoint
          },
          headers: getAuthHeaders(token)
        }
      );
    } catch (error) {
      const status = error.response?.status;

      if (![401, 404, 410].includes(status)) {
        throw error;
      }
    }

    await subscription.unsubscribe();
  } catch (error) {
    console.warn(
      'Could not remove push subscription:',
      error.response?.data || error.message
    );
  }
}

async function toggleNotifications() {
  if (notificationsEnabled.value) {
    notificationsEnabled.value = false;

    const key = notificationPreferenceKey();

    if (key) {
      localStorage.setItem(key, 'true');
    }

    try {

      await disableNotificationsOnServer();
    } catch (error) {
      console.warn(
        'Could not disable notifications on server:',
        error.response?.data || error.message
      );
    }

    await removePushSubscription();

    return;
  }

  await enableNotifications();
}

async function enableNotifications() {
  if (!('Notification' in window)) {
    notificationsEnabled.value = false;
    return;
  }

  try {
    let permission = Notification.permission;

    if (permission !== 'granted') {
      permission = await Notification.requestPermission();
    }

    if (permission !== 'granted') {
      notificationsEnabled.value = false;
      return;
    }

    const key = notificationPreferenceKey();

    if (key) {
      localStorage.removeItem(key);
    }

    await enableNotificationsOnServer();

    notificationsEnabled.value = true;

    await registerPushSubscription();
  } catch (error) {
    notificationsEnabled.value = false;

    console.warn(
      'Could not enable notifications:',
      error.response?.data || error.message
    );
  }
}

function startNotificationPolling() {
  const identity = getNotificationIdentity();

  if (!identity) return;
  if (notificationIdentity === identity) return;

  notificationIdentity = identity;

  if (notificationsEnabled.value) {
    void registerPushSubscription();
  }
}

function stopNotificationPolling() {
  notificationIdentity = '';
}

function refreshAuth() {
  const token = getToken();
  const role = getUserType();

  if (!token || !role) {
    isLoggedIn.value = false;
    userType.value = '';
    notificationsEnabled.value = false;
    return;
  }

  isLoggedIn.value = true;
  userType.value = role;

  const key = notificationPreferenceKey();

  notificationsEnabled.value =
    !key || localStorage.getItem(key) !== 'true';
}

async function logout() {

  const token = getToken();

  try {
    if (token) {

      await disableNotificationsOnServer(token);
    }

    await removePushSubscription();
  } catch (error) {
    console.warn(
      'Notification logout cleanup failed:',
      error.response?.data || error.message
    );
  } finally {
    stopNotificationPolling();

    sessionStorage.removeItem('token');
    sessionStorage.removeItem('userType');

    isLoggedIn.value = false;
    userType.value = '';
    notificationsEnabled.value = false;

    await router.push('/login');
  }
}

onMounted(() => {
  refreshAuth();

  document.addEventListener('click', unlockAudio, {
    once: true
  });

  if (isLoggedIn.value) {
    startNotificationPolling();
  }

  const navEl = document.getElementById('navbarNav');

  removeRouterAfterEach = router.afterEach(() => {
    refreshAuth();

    if (isLoggedIn.value) {
      startNotificationPolling();
    } else {
      stopNotificationPolling();
    }

    if (navEl) {
      const bsCollapse = Collapse.getInstance(navEl);

      if (bsCollapse) {
        bsCollapse.hide();
      }
    }
  });
});

onUnmounted(() => {
  stopNotificationPolling();

  document.removeEventListener('click', unlockAudio);

  if (removeRouterAfterEach) {
    removeRouterAfterEach();
  }
});
</script>

<style scoped>
.notification-setup {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin: 0 1rem 1rem;
}

.notification-bell {
  border: 0;
  font-size: 1.05rem;
  color: white;
}

.notification-bell.text-muted {
  color: white !important;
}
</style>
